import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getEmptyPage, getOriginalPage, getPageSize } from 'editor-utils/template';
import contains from 'utils/contains';
import { animateScroll } from 'react-scroll';
import styled from 'styled-components';
import { rgba } from 'utils/polished';
import PageWrapper from '../../components/pageWrapper';
import Page from '../../../components/page/preview';

const PageLabel = styled.span`
  width: 100%;
  padding-top: 10px;
  text-align: center;
  color: #26336b;
`;

const PageCell = styled.div`
  min-width: ${props => (props.$span ? 240 : 120)}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  * {
    user-select: none;
  }
  img {
    pointer-events: none;
  }
`;

const TopMenu = styled.div`
  top: -30px;
  position: absolute;
  text-align: center;
  width: 100%;
`;

const ToggleButton = styled.button`
  width: 140px;
  height: 30px;
  background: white;
  border: 1px solid #e4e5e5;
  border-bottom: 0;
  border-radius: 5px 5px 0 0;
  user-select: none;
  margin: 0 -0.5px;
`;

const PageManagerButton = styled.button`
  height: 25px;
  position: absolute;
  top: 4px;
  margin-left: 0px;
  width: 120px;
  font-size: 13px;
  border-radius: 0 5px 0 0;

  color: ${props => props.theme.colors.primary};
  background: ${props => rgba(props.theme.colors.primary, 0.16)};

  &:hover {
    color: ${props => props.theme.colors.white};
    background: ${props => props.theme.colors.primary};
  }
`;

const Thumbs = props => {
  const [originalCover, emptyPage, originalPages, originalBack] = useMemo(() => {
    let cover;
    let pages;
    if (props.template.cover) {
      [cover, ...pages] = props.pages;
    } else {
      pages = props.pages;
    }

    const [pageWidth, pageHeight] = getPageSize(props.template);

    return [
      cover
        ? (() => {
            const { size } = getOriginalPage(props.template, cover, 'cover');
            return { ...cover, label: 'Cover', size, type: 'cover', visible: true };
          })()
        : null,
      getEmptyPage(props.template),
      pages.map(page => {
        const size = { width: pageWidth, height: pageHeight };
        if (page.span) {
          size.width *= 2;
        }
        // return { label, ...originalPage }; // this is for CSS render
        return {
          ...page,
          label: page.pageLabel,
          size,
          type: 'pages',
          visible: true,
        }; // this is for ServerSide render
      }),
      props.template.back && cover.spine == null
        ? { label: 'Back', ...getOriginalPage(props.template, {}, 'back') }
        : null,
    ];
  }, [props.pages, props.template]);

  const setActivePage = page => props.onChangePage(page);

  const [show, setShow] = useState(true);

  const activePageKeys = useMemo(() => {
    return props.activePages.filter(({ visible }) => visible !== false).map(item => item.page.key);
  }, [props.activePages]);

  useEffect(() => {
    if (show) {
      const pageElement = document.getElementById(`thumb-${props.activePage}`);
      if (pageElement) {
        let offset = pageElement.offsetLeft - window.innerWidth / 2;
        if (typeof props.activePage === 'number') {
          if (props.activePage % 2 === 0) {
            offset += pageElement.offsetWidth;
          }
        }
        animateScroll.scrollTo(offset, {
          containerId: 'book-complex-thumbs',
          smooth: true,
          horizontal: true,
          // duration: 250,
          delay: 0,
        });
      }
    }
  }, [props.activePage, show]);

  return (
    <>
      <TopMenu>
        <ToggleButton onClick={() => setShow(val => !val)}>
          {show ? 'Hide Thumbnails' : 'Show Thumbnails'}
        </ToggleButton>
        {props.onExpand && (
          <PageManagerButton onClick={() => props.onExpand()}>Manage Pages</PageManagerButton>
        )}
      </TopMenu>
      {show && (
        <div id="book-complex-thumbs" className="custom-scrollbar-x">
          {originalCover && (
            <PageCell id="thumb-cover" className="mr-3" $span={originalCover.spine != null}>
              <PageWrapper noMenu>
                <Page
                  page={originalCover}
                  photos={props.photos}
                  template={props.template}
                  fullWidth
                  serverSideDPI={16}
                  onClick={() => setActivePage('cover')}
                  className={props.activePage === 'cover' && 'editor-page-active'}
                />
              </PageWrapper>
              <PageLabel>{originalCover.label}</PageLabel>
            </PageCell>
          )}

          {!props.config.withoutFirstBlankPage && (
            <PageCell>
              <PageWrapper noMenu>
                <Page page={emptyPage} fullWidth className="z-index-negative" />
              </PageWrapper>
            </PageCell>
          )}

          {originalPages.map((originalPage, index) => (
            <PageCell
              id={`thumb-${index + 1}`}
              key={originalPage.key}
              className={(index % 2 === 0 || originalPage.span) && 'mr-3'}
              $span={originalPage.span}
            >
              <PageWrapper noMenu>
                <Page
                  page={originalPage}
                  photos={props.photos}
                  template={props.template}
                  fullWidth
                  serverSideDPI={16}
                  onClick={() => setActivePage(index + 1)}
                  className={contains(activePageKeys, originalPage.key) && 'editor-page-active'}
                />
              </PageWrapper>
              <PageLabel>{originalPage.label}</PageLabel>
            </PageCell>
          ))}

          {!props.config.withoutFirstBlankPage && originalPages.length % 2 === 0 && (
            <PageCell className="mr-3">
              <PageWrapper noMenu>
                <Page page={emptyPage} fullWidth className="z-index-negative" />
              </PageWrapper>
            </PageCell>
          )}

          {originalBack && (
            <PageCell id="thumb-back">
              <PageWrapper noMenu>
                <Page
                  page={originalBack}
                  template={props.template}
                  fullWidth
                  onClick={() => setActivePage('back')}
                  className={props.activePage === 'back' && 'editor-page-active'}
                />
              </PageWrapper>
              <PageLabel>{originalBack.label}</PageLabel>
            </PageCell>
          )}
        </div>
      )}
    </>
  );
};

Thumbs.propTypes = {
  activePage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  activePages: PropTypes.array,
  template: PropTypes.object,
  config: PropTypes.object,
  pages: PropTypes.array,
  photos: PropTypes.array,
  onChangePage: PropTypes.func,
  onExpand: PropTypes.func,
};

export default Thumbs;
